import React, { useContext, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Panel } from 'primereact/panel';
import { Formik } from 'formik';
import _ from 'lodash';
import { growlErrors } from '../../../utils/NotificationUtil';
import NewRespondentForm from './NewRespondentForm';
import buildValidationSchema from './validation';
import SubmitDeclarationModal from '../../Accounts/ARI/SubmitDeclarationModal';
import hashCode from '../../../utils/StringUtil';
import { useParams, useHistory } from "react-router-dom";
import { AccountContext } from '../../../components/AccountContext';
import { NotificationContext } from '../../../components/GrowlNotifications';
import { useSelector } from 'react-redux';
import { searchJobProfile } from '../../../services/apps/JobProfileService';
import { AppConstants, RespondentType } from '../../../shared/Constants';

const NewRespondent = ({ onCancel, onSave, accountData, agreementData }) => {
  const [jobs, setJobs] = useState([]);
  const [jobSearching, setJobSearching] = useState(false);

  const { aid } = useParams();
  const history = useHistory();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { formatMessage } = useIntl();

  const {
    cldr: { localeDisplayName },
  } = useSelector(state => state.i18n);

  useEffect(() => {
    searchJob();
  // eslint-disable-next-line
  }, []);

  const searchJob = async (query) => {
    setJobSearching(true);

    try {
      const data = await searchJobProfile({
        aid,
        query,
      });

      setJobs(data);
    } catch (err) {
      growlErrors(growl, err);
    } finally {
      setJobSearching(false);
    }
  }

  const validationSchema = buildValidationSchema(formatMessage, accountData);

  const initialValues = {
    firstName: '',
    lastName: '',
    email: '',
    notes: '',
    locale: AppConstants.DEFAULT_LOCALE,
    type: RespondentType.EXTERNAL,
    gender: 'U',
    targetJob: '',
  };

  let fieldsInfo = [];

  accountData.account.globalAriFields.forEach((field) => {
    initialValues[`global_${field.id}_${hashCode(field.question)}`] = '';
    fieldsInfo = [...fieldsInfo, {
      ariFieldId: field.id,
      question: field.question,
      mandatory: field.mandatory,
      ariInputType: field.dataType,
      dropdownAnswers: field.answers,
    }];
  });

  accountData.account.localAriFields.forEach((field) => {
    initialValues[`local_${field.id}_${hashCode(field.question)}`] = '';
    fieldsInfo = [...fieldsInfo, {
      ariFieldId: field.id,
      question: field.question,
      mandatory: field.mandatory,
      ariInputType: field.dataType,
      dropdownAnswers: field.answers,
    }];
  });

  const agreed = _.find(agreementData.viewer.accounts.edges,
    edge => edge.node.id === currentAccount).agreedToGdprTerms;

  const preferedLocales = accountData.activeLocales.map(lang => ({
    value: lang,
    label: localeDisplayName(lang),
  }));

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <div className="wrapper p-grid">
            <div className="p-col-12">
              <Panel header={<FormattedMessage id="candidate.new" defaultMessage="Add Respondent" />}>
                <Formik
                  onSubmit={(values, formikBag) => {
                    const { resetForm } = formikBag;
                    onSave(values, fieldsInfo);
                    resetForm();
                  }}
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                >
                  {() => (
                    <NewRespondentForm
                      jobs={jobs}
                      jobSearching={jobSearching}
                      preferedLocales={preferedLocales}
                      onCancel={onCancel}
                      searchJob={(query) => searchJob(query)}
                    />
                  )}
                </Formik>
                <SubmitDeclarationModal
                  isVisible={!agreed}
                  onCancel={() => { history.push(`/accounts/${currentAccount}/respondents`); }}
                />
              </Panel>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

NewRespondent.propTypes = {
  accountData: PropTypes.objectOf(PropTypes.any).isRequired,
  agreementData: PropTypes.objectOf(PropTypes.any).isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default NewRespondent;
